import filesize from "filesize";
import moment from "moment-timezone";
import _ from "lodash";

import { ORG_TYPES, MARKETS } from "../views/Pages/OrganizationPageView";

import Logger from "../lib/logger";
import { WILDCARD_ORG_ID } from "lib/api/API";
const logger = Logger("utils/index");

export function singleLineString(strings, ...values) {
  // Interweave the strings with the
  // substitution vars first.
  let output = "";
  for (let i = 0; i < values.length; i += 1) {
    output += strings[i] + values[i];
  }
  output += strings[values.length];

  // Split on newlines.
  const lines = output.split(/(?:\r\n|\n|\r)/);

  // Rip out the leading whitespace.
  return lines
    .map((line) => line.replace(/^\s+/gm, ""))
    .join(" ")
    .trim();
}

// Takes a number (of bytes), and returns it as a string in KiB, MiB, etc.
export const formatBytes = (num, precision = 2) => {
  if (num === "" || num === undefined) {
    return "";
  }

  return filesize(num, {
    locale: true,
    round: precision,
    standard: "iec",
  });
};

// A constant used in formatNumber
const JEDEC_TO_CUSTOM_SYMBOLS = {
  B: " ",
  kB: "K",
  MB: "M",
  GB: "G",
  TB: "T",
  PB: "P",
  EB: "E",
  ZB: "Z",
  YB: "Y",
};
// Takes a number (not byte number), and returns it as a string
// with JEDEC unit abbreviations mapped to custom ones
export const formatNumber = (num, precision = 2) => {
  if (!num && num !== 0) {
    return "";
  }

  return filesize(num, {
    base: 10,
    round: precision,
    symbols: JEDEC_TO_CUSTOM_SYMBOLS,
  });
};

// Takes a string of decimal digits.
// Returns the string's truncation to 'maxLength' digits, and a units suffix.
export const formatNumberCustom = (inputStr, maxLength = 9) => {
  const sizeNames = ["K", "M", "G", "T", "P"];

  const out = {
    value: inputStr || "",
    suffix: "",
  };

  for (let i = 0; i < sizeNames.length; i += 1) {
    if (out.value.length <= maxLength) {
      return out;
    }

    out.value = out.value.slice(0, -3);
    out.suffix = sizeNames[i];
  }

  return out;
};

export const composePath = (event, nodes = []) => {
  if (nodes && nodes.length > 0) {
    const element = nodes[nodes.length - 1];

    if (element.parentElement.tagName === "HTML") {
      return nodes.concat(element.parentElement);
    }

    return composePath(event, nodes.concat([element.parentElement]));
  }

  return composePath(event, [event.target]);
};

export const composedPath = (event) => {
  if (event.composedPath && typeof event.composedPath === "function") {
    return event.composedPath();
  }

  if (event.path && Array.isArray(event.path)) {
    return event.path;
  }

  return composePath(event);
};

/*
Returns true if `evt.target` is inside `domElt`.
Can be replaced with evt.composedPath() check for everything except IE.
*/
export const isInside = (evt, domElt) => {
  if (!evt) {
    return false;
  }

  let current = evt.target;

  while (current) {
    if (current === domElt) {
      return true;
    }
    current = current.parentNode;
  }

  return false;
};

// Returns a formatted date string in UTC if guessTz is falsy,
// or in the user's timezone if guessTz is truthy
export const formatDate = ({ date, formatStr, guessTz = false }) => {
  let momentObj;

  try {
    momentObj = moment.utc(date);
  } catch (err) {
    logger.error("formatDate received invalid date", err);

    return "";
  }

  if (!momentObj.isValid()) {
    return "";
  }

  if (guessTz) {
    return momentObj.tz(moment.tz.guess()).format(formatStr);
  }

  return momentObj.format(formatStr);
};

export const formatDateUserTz = (date) =>
  formatDate({
    date,
    formatStr: "lll z",
    guessTz: true,
  });

const NO_EPS_TEXT = "Not available";

// Returns events-per-second as a string
export const getEps = ({ logsTotal, windowEnd, windowStart }) => {
  let end;
  let start;

  try {
    end = moment.utc(windowEnd);
    start = moment.utc(windowStart);
  } catch (err) {
    logger.error(
      "Sensor support EventsPerSecond received invalid time window",
      err
    );
    return NO_EPS_TEXT;
  }

  const delta = end.diff(start, "seconds");

  const eps = Math.round((logsTotal || 0) / delta);

  return eps;
};

export const isNumber = (num) => /^-?[\d.]+(?:e-?\d+)?$/.test(num);

const fallbackCopyTextToClipboard = (text) => {
  var textArea = document.createElement("textarea");
  textArea.value = text;

  // Avoid scrolling to bottom
  textArea.style.top = "0";
  textArea.style.left = "0";
  textArea.style.position = "fixed";

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  document.execCommand("copy");
  document.body.removeChild(textArea);
};

export const copyTextToClipboard = (text) => {
  if (!navigator.clipboard) {
    fallbackCopyTextToClipboard(text);
    return;
  }
  navigator.clipboard.writeText(text);
};
export const getValue = _.curry((defaultValue, value) => {
  return value ?? defaultValue;
});

export const isOrganizationChildOfNFR = (organization = {}) => {
  return organization.market === 20 && !!organization.parentId;
};

export const renderModifiedCell = (column, model) => {
  var timeValue = model[column.field];
  if (!timeValue) {
    timeValue = model["created"];
  }
  return moment.utc(timeValue).tz(moment.tz.guess(true)).format("lll z");
};

export const getHeapUserProperties = (user, organization) => {
  const propertiesToReturn = {
    "First Name": user.first_name,
    "Last Name": user.last_name,
    Email: user.email,
    Title: user.title || "",
    "Super Admin": !!user.superadmin,
    "Organization Name": "Unknown",
    "Organization ID": organization ? organization.id : WILDCARD_ORG_ID,
    "Organization Roles": [],
  };

  if (organization) {
    // default market to "Direct"
    const activeOrgMarket = _.get(organization, ["market"], 0);

    // default to "Paying Customer"
    const activeOrgType = _.get(organization, ["orgType"], "normal");

    // default to 'ADVANCED' since default behavior is to grant full access
    const activeOrgLicense = _.get(
      organization,
      ["config", "license"],
      "ADVANCED"
    );

    propertiesToReturn["Edition"] = activeOrgLicense;
    propertiesToReturn["Organization Name"] = organization.name;
    propertiesToReturn["Market"] = MARKETS.find(
      ({ value }) => value === activeOrgMarket
    ).label;
    propertiesToReturn["Account Type"] = ORG_TYPES.find(
      ({ value }) => value === activeOrgType
    ).label;
    propertiesToReturn["Organization Roles"] = user.orgRoles
      .filter((role) => role.orgId === organization.id)
      .map((orgRole) => orgRole.roleName);
  } else {
    propertiesToReturn["Organization Roles"] = ["admin"];
    propertiesToReturn["Organization Name"] = "Acting Super Admin";
  }

  return propertiesToReturn;
};

export const getUrlForNewTab = (urlParamData) => {
  const {
    location: { origin },
  } = window;
  const { id1, orgId, topLevel, secondLevel } = urlParamData;

  // start url array with base params
  const urlArray = [origin, orgId];

  if (topLevel) urlArray.push(topLevel);

  if (secondLevel) urlArray.push(secondLevel);

  // id1 for viewing details i.e. from sensors of findings
  if (id1) urlArray.push(id1);

  // build array with params
  return urlArray.join("/");
};
